// MARK://dhk8/pos/components/PosLayout
// The ONE POS shell — same surface for cashier, manager, and training mode.
// Topbar (brand + training badge + user) over an icon rail (left) + content.
//
// Visual style: HIG-green (Apple HIG geometry + nintaigo green palette), tuned
// for the tablet portrait target 534×738 @ DPR 1.5 (`data-density="compact"`).
// All measurements come from `styles/tokens.css` (`--pos-*`). Touch targets are
// ≥44pt (the icon-rail items are 44×52).
//
// Phone breakpoint (≤520px): the left rail is hidden and replaced by a hamburger
// in the topbar that opens a slide-over drawer with the full nav. The 534-wide
// phablet keeps the rail exactly as before — the phone behavior is purely
// additive (media queries + a tiny bit of drawer state).
//
// The nav list is filtered through `lib/role-license-gate.ts` so kitchen-role
// users see only `/kds`, license-gated entries (`/reports`, printing) hide when
// the tenant lacks the feature, and role deny-lists prune the rest.

import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { usePendingAcceptanceCount } from '../hooks/usePendingAcceptanceCount.js'
import { useFloorAlertsCount } from '../hooks/useFloorAlertsCount.js'
import { LayoutGrid, ChefHat, ShoppingBag, Receipt, DollarSign, BarChart3, LogOut, Shield, Printer, Palette, CloudOff, Flame, Coffee, FileText, Lock, Bell, Package, Menu as MenuIcon, X, ExternalLink, Calendar, Clock as ClockIcon, RefreshCw, Stethoscope, HandPlatter } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import type { LucideIcon } from 'lucide-react'

import { useAuthStore } from '../stores/auth-store.js'
import { useLockStore } from '../stores/lock-store.js'
import { t } from '../i18n/es-MX.js'
import { canAccessRoute } from '../lib/role-license-gate.js'
import { operatingMode } from '../lib/api-origin.js'
import PosOverlays from '../features/pos/PosOverlays.js'
import { useTrainingMode } from '../lib/training-mode.js'
import AvatarMenu from './AvatarMenu.js'
import { BranchSwitcher } from './BranchSwitcher.js'
import BrandMark from './BrandMark.js'
import TrainingModeBadge from './TrainingModeBadge.js'
import ThemeSwitcher from './ThemeSwitcher.js'

interface PosLayoutProps {
  children: React.ReactNode
}

interface NavItem {
  path: string
  label: string
  Icon: LucideIcon
}

// Audited against the routes in App.tsx. Order is operación → caja → fiscal → reportes →
// admin → sistema. Every entry maps to a real shelled page; flow-only routes
// (/shift/select) and sub-pages (/reports/hub, /tickets/:id, /billing/*) are intentionally
// not in the rail. The list is filtered through role-license-gate.canAccessRoute().
const NAV_ITEMS: readonly NavItem[] = [
  // operación
  { path: '/floor', label: t('nav.floor'), Icon: LayoutGrid },
  // Lane #164 — vistas standalone (no más atajos a drawers).
  { path: '/reservations', label: t('nav.reservations'), Icon: Calendar },
  { path: '/waitlist',     label: t('nav.waitlist'),     Icon: ClockIcon },
  { path: '/avisos', label: t('nav.avisos'), Icon: Bell },
  { path: '/para-llevar', label: t('nav.takeout'), Icon: Package },
  { path: '/orders/pending', label: t('nav.pendingOrders'), Icon: HandPlatter },
  { path: '/menu', label: t('nav.menu'), Icon: ChefHat },
  { path: '/order', label: t('nav.order'), Icon: ShoppingBag },
  { path: '/pos/barra', label: t('nav.barra'), Icon: Coffee },
  { path: '/kds', label: t('nav.kds'), Icon: Flame },
  { path: '/tickets', label: t('nav.tickets'), Icon: Receipt },
  // caja
  { path: '/cashcut', label: t('nav.cashcut'), Icon: DollarSign },
  // fiscal
  { path: '/billing', label: t('nav.billing'), Icon: FileText },
  // reportes
  { path: '/reports', label: t('nav.reports'), Icon: BarChart3 },
  // admin
  { path: '/manager', label: t('nav.manager'), Icon: Shield },
  { path: '/settings/printing', label: t('nav.printing'), Icon: Printer },
  { path: '/settings/appearance', label: t('nav.appearance'), Icon: Palette },
  // sistema
  { path: '/offline/cola', label: t('nav.connection'), Icon: CloudOff },
  // Diagnóstico de impresión — visible para TODOS los roles (mesero incluido).
  { path: '/diagnostico', label: t('nav.diagnostics'), Icon: Stethoscope },
]

// The merchant admin SPA — opened in a new tab (it's a separate origin/app, not a route
// in this PWA). Only shown in cloud-direct mode (the LAN edge doesn't host the admin) and
// only to owners/managers. Sits alongside the theme agent's `/settings/appearance` item.
const ADMIN_PANEL_URL = 'https://admin.nintaigo.com'
const ADMIN_ROLES = new Set(['owner', 'manager', 'gerente'])

function canSeeAdminPanel(user: { role?: string } | null): boolean {
  if (!user) return false
  if (operatingMode() !== 'cloud-direct') return false
  return ADMIN_ROLES.has((user.role ?? '').trim().toLowerCase())
}

export default function PosLayout({ children }: PosLayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const lockTerminal = useLockStore((s) => s.lock)
  const training = useTrainingMode()
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Lane #170 — soft-refresh: invalida todas las queries (react-query) sin
  // recargar el browser. Evita F5 que dispara lockscreen + pierde JWT
  // residual + recarga assets innecesariamente. Throttle 3s para evitar
  // spam taps que tumben el backend.
  const queryClient = useQueryClient()
  const [refreshing, setRefreshing] = useState(false)
  const [lastRefreshAt, setLastRefreshAt] = useState(0)
  function handleSoftRefresh() {
    const now = Date.now()
    if (refreshing || now - lastRefreshAt < 3000) return
    setRefreshing(true)
    setLastRefreshAt(now)
    void queryClient.invalidateQueries()
      .then(() => queryClient.refetchQueries({ type: 'active' }))
      .finally(() => setTimeout(() => setRefreshing(false), 400))
  }

  function handleLogout() {
    setDrawerOpen(false)
    logout()
    navigate('/login', { replace: true })
  }

  // Close the drawer whenever the route changes (selecting a nav item navigates).
  useEffect(() => { setDrawerOpen(false) }, [location.pathname])

  // Escape closes the drawer (nice-to-have).
  useEffect(() => {
    if (!drawerOpen) return
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setDrawerOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [drawerOpen])

  const visibleNav = NAV_ITEMS.filter((item) => canAccessRoute(item.path, user))
  // Bolita VIVA de "órdenes por aceptar" (WS → refetch REST autoritativo).
  const pendingAcceptance = usePendingAcceptanceCount()
  const floorAlerts = useFloorAlertsCount()
  const showAdminPanel = canSeeAdminPanel(user)

  return (
    <div className="pos-shell" data-training={training ? 'on' : undefined}>
      <header className="pos-topbar">
        <div className="pos-topbar__brand">
          <button
            type="button"
            className="pos-topbar__burger"
            aria-label="Abrir navegación"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon size={22} aria-hidden />
          </button>
          <BrandMark size="md" />
          <span className="pos-topbar__tag">restaurante</span>
        </div>
        <div className="pos-topbar__center">
          {/* Sucursal SIEMPRE a la vista: el plano, los QRs y la caja son de ésta. */}
          <BranchSwitcher />
          <TrainingModeBadge />
        </div>
        <div className="pos-topbar__meta">
          <button
            type="button"
            className={`pos-topbar__refresh${refreshing ? ' is-spinning' : ''}`}
            aria-label="Refrescar datos sin recargar la página"
            title={refreshing ? 'Refrescando…' : 'Refrescar datos'}
            onClick={handleSoftRefresh}
            disabled={refreshing}
          >
            <RefreshCw size={18} aria-hidden />
          </button>
          {user && (
            <button
              type="button"
              className="pos-topbar__lock"
              aria-label={t('nav.lock')}
              title={t('nav.lock')}
              onClick={() => { setDrawerOpen(false); lockTerminal() }}
            >
              <Lock size={18} aria-hidden />
            </button>
          )}
          <ThemeSwitcher />
          {/* Avatar (iniciales) → menú de cuenta: toggle Pruebas/Vivo + refrescar +
              tema + bloquear + cerrar sesión. Reemplaza el nombre+dominio sueltos. */}
          <AvatarMenu
            onRefresh={handleSoftRefresh}
            refreshing={refreshing}
            onLock={() => { setDrawerOpen(false); lockTerminal() }}
            onLogout={handleLogout}
          />
        </div>
      </header>

      <div className="pos-body">
        <nav className="pos-rail" aria-label="Navegación principal">
          {visibleNav.map(({ path, label, Icon }) => {
            // Match exacto para /floor para no marcar /floor/qr-codes como activo.
            // Para los demás: exacto o anidado por LÍMITE DE SEGMENTO — nunca por
            // prefijo de string (si no, /order marcaría /orders/pending como activo).
            const active = path === '/floor'
              ? location.pathname === '/floor'
              : location.pathname === path || location.pathname.startsWith(`${path}/`)
            return (
              <Link
                key={path}
                to={path}
                className={`pos-rail__item${active ? ' pos-rail__item--active' : ''}`}
                aria-current={active ? 'page' : undefined}
              >
                {(() => {
                  // Bolita roja de conteo: órdenes por aceptar en "Entrantes",
                  // avisos del comensal pendientes en "Avisos". Misma UX.
                  const badgeCount = path === '/orders/pending' ? pendingAcceptance
                    : path === '/avisos' ? floorAlerts
                    : 0
                  const badgeLabel = path === '/orders/pending' ? 'órdenes por aceptar' : 'avisos pendientes'
                  return (
                    <span style={{ position: 'relative', display: 'inline-flex' }}>
                      <Icon size={20} aria-hidden />
                      {badgeCount > 0 && (
                        <span
                          aria-label={`${badgeCount} ${badgeLabel}`}
                          style={{
                            position: 'absolute', top: -7, right: -11,
                            minWidth: 16, height: 16, padding: '0 4px',
                            borderRadius: 8, background: 'var(--danger, #dc2626)',
                            color: '#fff', fontSize: 10, fontWeight: 700,
                            lineHeight: '16px', textAlign: 'center',
                            boxShadow: '0 0 0 2px var(--surface, #fff)',
                          }}
                        >
                          {badgeCount > 99 ? '99+' : badgeCount}
                        </span>
                      )}
                    </span>
                  )
                })()}
                <span className="pos-rail__label">{label}</span>
              </Link>
            )
          })}
          {showAdminPanel && (
            <a
              href={ADMIN_PANEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="pos-rail__item"
              title={t('nav.admin')}
            >
              <ExternalLink size={20} aria-hidden />
              <span className="pos-rail__label">{t('nav.adminShort')}</span>
            </a>
          )}
          <div className="pos-rail__spacer" />
          <button type="button" className="pos-rail__item pos-rail__item--logout" onClick={handleLogout}>
            <LogOut size={20} aria-hidden />
            <span className="pos-rail__label">{t('nav.logout')}</span>
          </button>
        </nav>
        <main className="pos-content">{children}</main>
      </div>

      {/* Phone nav drawer — rendered only below the breakpoint (hidden via CSS
          above 520px). A scrim + a left slide-over with the full nav list. */}
      <div className={`pos-drawer${drawerOpen ? ' pos-drawer--open' : ''}`} aria-hidden={!drawerOpen}>
        <button
          type="button"
          className="pos-drawer__scrim"
          aria-label="Cerrar navegación"
          tabIndex={drawerOpen ? 0 : -1}
          onClick={() => setDrawerOpen(false)}
        />
        <nav className="pos-drawer__panel" aria-label="Navegación principal">
          <div className="pos-drawer__head">
            <BrandMark size="md" />
            <button
              type="button"
              className="pos-drawer__close"
              aria-label="Cerrar navegación"
              tabIndex={drawerOpen ? 0 : -1}
              onClick={() => setDrawerOpen(false)}
            >
              <X size={20} aria-hidden />
            </button>
          </div>
          <div className="pos-drawer__list">
            {visibleNav.map(({ path, label, Icon }) => {
              const active = path === '/floor'
                ? location.pathname === '/floor'
                : location.pathname === path || location.pathname.startsWith(`${path}/`)
              return (
                <Link
                  key={path}
                  to={path}
                  className={`pos-drawer__item${active ? ' pos-drawer__item--active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  tabIndex={drawerOpen ? 0 : -1}
                  onClick={() => setDrawerOpen(false)}
                >
                  <Icon size={20} aria-hidden />
                  <span>{label}</span>
                </Link>
              )
            })}
            {showAdminPanel && (
              <a
                href={ADMIN_PANEL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="pos-drawer__item"
                tabIndex={drawerOpen ? 0 : -1}
                onClick={() => setDrawerOpen(false)}
              >
                <ExternalLink size={20} aria-hidden />
                <span>{t('nav.admin')}</span>
              </a>
            )}
          </div>
          <div className="pos-drawer__foot">
            <button
              type="button"
              className="pos-drawer__item pos-drawer__item--logout"
              tabIndex={drawerOpen ? 0 : -1}
              onClick={handleLogout}
            >
              <LogOut size={20} aria-hidden />
              <span>{t('nav.logout')}</span>
            </button>
          </div>
        </nav>
      </div>

      {/* Global overlays (terminal lock + keyboard shortcuts) — mounted once at the
          shell level so they cover every route, not just the cashier pages. */}
      <PosOverlays />

      <style>{shellStyles}</style>
    </div>
  )
}

const shellStyles = `
  /* HIG-green shell. All values come from styles/tokens.css (--pos-*). This
     component must NOT declare a :root token block — tokens.css owns them. */
  .pos-shell {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100dvh;
    width: 100%;
    background: var(--pos-canvas);
    color: var(--pos-fg);
    font-family: var(--font-sans);
    overflow: hidden;
  }

  /* ── Topbar ─────────────────────────────────────────────────────────── */
  .pos-topbar {
    flex-shrink: 0;
    height: var(--pos-topbar-h);
    display: flex;
    align-items: center;
    gap: var(--pos-space-2);
    padding: 0 var(--pos-space-4);
    background: var(--pos-surface);
    border-bottom: 1px solid var(--pos-separator);
    box-shadow: var(--pos-shadow-1);
    z-index: var(--pos-z-topbar);
  }
  /* Brand (hamburguesa + logo) NO se encoge: siempre visible, nunca aplastado por el meta. */
  .pos-topbar__brand { display: flex; align-items: center; gap: var(--pos-space-2); min-width: 0; flex-shrink: 0; }
  /* Hamburger — only on phones (≤520px). 44pt touch target. */
  .pos-topbar__burger {
    display: none;
    align-items: center; justify-content: center;
    width: 44px; height: 44px; margin-left: calc(var(--pos-space-2) * -1);
    border: none; border-radius: var(--pos-radius-md);
    background: transparent; color: var(--pos-fg-secondary); cursor: pointer;
    transition: background var(--pos-duration-fast) var(--pos-ease-out), color var(--pos-duration-fast) var(--pos-ease-out);
  }
  .pos-topbar__burger:hover { background: var(--pos-elevated); color: var(--pos-fg); }
  .pos-topbar__burger:active { transform: scale(.95); }
  /* Manual terminal-lock — 44pt touch target, lives in the topbar meta cluster. */
  .pos-topbar__lock,
  .pos-topbar__refresh {
    display: inline-flex; align-items: center; justify-content: center;
    width: 44px; height: 44px;
    border: none; border-radius: var(--pos-radius-md);
    background: transparent; color: var(--pos-fg-secondary); cursor: pointer;
    transition: background var(--pos-duration-fast) var(--pos-ease-out), color var(--pos-duration-fast) var(--pos-ease-out);
  }
  .pos-topbar__lock:hover,
  .pos-topbar__refresh:hover { background: var(--pos-elevated); color: var(--pos-fg); }
  .pos-topbar__lock:active,
  .pos-topbar__refresh:active { transform: scale(.95); }
  .pos-topbar__refresh:disabled { cursor: default; opacity: .7; }
  /* Lane #170 — spinner cuando refresca. Animación CSS pura sobre el SVG. */
  .pos-topbar__refresh.is-spinning svg { animation: pos-topbar-spin 750ms linear infinite; }
  @keyframes pos-topbar-spin {
    from { transform: rotate(0); }
    to   { transform: rotate(360deg); }
  }
  @media (prefers-reduced-motion: reduce) {
    .pos-topbar__refresh.is-spinning svg { animation: none; }
  }
  .pos-topbar__tag {
    font-family: var(--font-display, var(--font-sans));
    font-size: var(--pos-text-xxs);
    letter-spacing: var(--pos-tracking-widest);
    text-transform: uppercase;
    color: var(--pos-fg-tertiary);
    white-space: nowrap;
  }
  /* Centro = espaciador dinámico (sostiene el badge de training cuando aplica). */
  .pos-topbar__center { display: flex; flex: 1; justify-content: center; align-items: center; min-width: 0; }
  /* Meta (refresh + lock + theme + avatar) NO se encoge tampoco. */
  .pos-topbar__meta {
    display: flex; flex-direction: row; align-items: center; gap: var(--pos-space-2);
    flex-shrink: 0; min-width: 0;
  }
  .pos-topbar__id { display: flex; flex-direction: column; align-items: flex-end; min-width: 0; line-height: var(--pos-leading-tight); }
  .pos-topbar__user { font-size: var(--pos-text-xs); font-weight: var(--pos-weight-medium); color: var(--pos-fg-secondary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 14ch; }
  .pos-topbar__domain { font-size: var(--pos-text-xxs); color: var(--pos-fg-tertiary); white-space: nowrap; }

  /* ── Body: icon rail + content ──────────────────────────────────────── */
  .pos-body { display: flex; flex: 1; min-height: 0; }

  .pos-rail {
    flex-shrink: 0;
    width: var(--pos-sidebar-w);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--pos-space-1);
    padding: var(--pos-space-2) var(--pos-space-1);
    background: var(--pos-surface);
    border-right: 1px solid var(--pos-separator);
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    z-index: var(--pos-z-rail);
    scrollbar-width: thin;
    scrollbar-color: var(--pos-separator) transparent;
  }
  .pos-rail::-webkit-scrollbar { width: 5px; }
  .pos-rail::-webkit-scrollbar-thumb { background: var(--pos-separator); border-radius: 3px; }
  .pos-rail::-webkit-scrollbar-track { background: transparent; }
  .pos-rail__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    width: 60px;
    min-height: 52px;            /* ≥44pt touch target + label */
    padding: var(--pos-space-2) 2px;
    border: none;
    border-radius: var(--pos-radius-md);
    background: transparent;
    color: var(--pos-fg-tertiary);
    font-family: var(--font-display, var(--font-sans));
    font-size: var(--pos-text-xxs);
    font-weight: var(--pos-weight-medium);
    letter-spacing: var(--pos-tracking-snug);
    text-decoration: none;
    cursor: pointer;
    transition: color var(--pos-duration-fast) var(--pos-ease-out),
                background var(--pos-duration-fast) var(--pos-ease-out);
  }
  .pos-rail__label { line-height: 1; }
  .pos-rail__item:hover { color: var(--pos-fg); background: var(--pos-elevated); }
  .pos-rail__item:active { transform: scale(.97); }
  /* Selected state: a full, rounded tint fill (HIG sidebar style) — no one-sided edge. */
  .pos-rail__item--active {
    color: var(--pos-tint-on);
    background: var(--pos-tint-fill);
  }
  .pos-rail__item--active:hover { background: var(--pos-tint-fill-hover); }
  .pos-rail__spacer { flex: 1; }
  .pos-rail__item--logout { color: var(--pos-fg-tertiary); }
  .pos-rail__item--logout:hover { color: var(--pos-danger); background: var(--pos-elevated); }

  /* ── Content ────────────────────────────────────────────────────────── */
  .pos-content {
    flex: 1;
    /* Stack children (banners + page) vertically so when SandboxBanner / TrainingBanner
       are rendered, the actual page below still gets the remaining height — otherwise
       pages with sticky bottom footers (Cobrar button) overflow off-screen. */
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    padding: var(--pos-content-pad);
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }
  /* The page wrapper (immediate child after any banners) must claim the leftover
     height so sticky footers stick to the viewport bottom, not the document bottom. */
  .pos-content > :last-child:not(.pos-sandbox-banner):not(.pos-training-banner) { flex: 1; min-height: 0; display: flex; flex-direction: column; }
  /* When training mode is on, the topbar carries the striped band 4px tall —
     nudge the content so the band never overlaps the first row of a page. */
  .pos-shell[data-training="on"] .pos-content { padding-top: calc(var(--pos-content-pad) + 4px); }

  /* ── Phone nav drawer ───────────────────────────────────────────────── */
  /* Hidden by default (the rail is visible). The phone media query below
     flips this on. We keep the element mounted so the slide can animate. */
  .pos-drawer { display: none; }
  .pos-drawer__scrim {
    position: fixed; inset: 0; border: none; padding: 0; margin: 0;
    background: var(--pos-overlay); cursor: pointer;
    opacity: 0; pointer-events: none;
    transition: opacity var(--pos-duration-base) var(--pos-ease-in-out);
    z-index: var(--pos-z-drawer-scrim);
  }
  .pos-drawer__panel {
    position: fixed; top: 0; left: 0; bottom: 0;
    width: min(82vw, 300px);
    display: flex; flex-direction: column;
    background: var(--pos-surface);
    border-right: 1px solid var(--pos-separator);
    box-shadow: var(--pos-shadow-4, 0 24px 60px -16px rgba(0,0,0,.55));
    transform: translateX(-100%);
    transition: transform var(--pos-duration-base) var(--pos-ease-out);
    z-index: var(--pos-z-drawer);
  }
  .pos-drawer--open .pos-drawer__scrim { opacity: 1; pointer-events: auto; }
  .pos-drawer--open .pos-drawer__panel { transform: translateX(0); }
  .pos-drawer__head {
    flex-shrink: 0; display: flex; align-items: center; justify-content: space-between;
    gap: var(--pos-space-2); height: var(--pos-topbar-h); padding: 0 var(--pos-space-3) 0 var(--pos-space-4);
    border-bottom: 1px solid var(--pos-separator);
  }
  .pos-drawer__close {
    display: flex; align-items: center; justify-content: center;
    width: 44px; height: 44px; border: none; border-radius: var(--pos-radius-md);
    background: transparent; color: var(--pos-fg-secondary); cursor: pointer;
    transition: background var(--pos-duration-fast) var(--pos-ease-out), color var(--pos-duration-fast) var(--pos-ease-out);
  }
  .pos-drawer__close:hover { background: var(--pos-elevated); color: var(--pos-fg); }
  .pos-drawer__list {
    flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    display: flex; flex-direction: column; gap: 2px;
    padding: var(--pos-space-2);
  }
  .pos-drawer__foot { flex-shrink: 0; padding: var(--pos-space-2); border-top: 1px solid var(--pos-separator); }
  .pos-drawer__item {
    display: flex; align-items: center; gap: var(--pos-space-3);
    min-height: 44px; padding: 0 var(--pos-space-3);
    border: none; border-radius: var(--pos-radius-md);
    background: transparent; color: var(--pos-fg-secondary);
    font-family: var(--font-display, var(--font-sans));
    font-size: var(--pos-text-sm); font-weight: var(--pos-weight-medium);
    text-decoration: none; text-align: left; width: 100%; cursor: pointer;
    transition: color var(--pos-duration-fast) var(--pos-ease-out), background var(--pos-duration-fast) var(--pos-ease-out);
  }
  .pos-drawer__item:hover { color: var(--pos-fg); background: var(--pos-elevated); }
  .pos-drawer__item:active { transform: scale(.99); }
  /* Active: full rounded tint fill — NO left edge accent. */
  .pos-drawer__item--active { color: var(--pos-tint-on); background: var(--pos-tint-fill); }
  .pos-drawer__item--active:hover { background: var(--pos-tint-fill-hover); }
  .pos-drawer__item--logout { color: var(--pos-fg-tertiary); }
  .pos-drawer__item--logout:hover { color: var(--pos-danger); background: var(--pos-elevated); }

  /* ── Phone breakpoint (≤520px): rail → drawer ───────────────────────── */
  @media (max-width: 520px) {
    .pos-rail { display: none; }
    .pos-topbar__burger { display: inline-flex; }
    .pos-drawer { display: block; }
    /* Brand "tag" + the domain line are noise at 375px — drop them. */
    .pos-topbar__tag { display: none; }
    .pos-topbar__domain { display: none; }
    .pos-topbar { padding: 0 var(--pos-space-3); gap: var(--pos-space-1); }
    /* Iconos un poco más chicos en móvil para que quepan burger+logo + meta. */
    .pos-topbar__lock, .pos-topbar__refresh { width: 38px; height: 38px; }
    .pos-topbar__meta { gap: var(--pos-space-1); }
  }
  @media (prefers-reduced-motion: reduce) {
    .pos-drawer__panel, .pos-drawer__scrim { transition: none; }
  }
`
